import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { useSiteStore } from '../../store/site.store';
import { api } from '../../services/api';

interface SiteReadinessHistoryPoint {
  date: string;
  overallScore: number;
  pillars: {
    indexability: number;
    contentIntent: number;
    technicalHygiene: number;
    internalLinking: number;
    performance: number;
    aiSearchability: number;
  };
  pagesAnalyzed: number;
  readyPageCount: number;
  partialPageCount: number;
  notReadyPageCount: number;
  criticalPageCount: number;
  failedCheckCount: number;
  warningCheckCount: number;
  openActionCount: number;
  completedActionCount: number;
  autoResolvedActionCount: number;
  openAffectedPageCount: number;
  resolvedAffectedPageCount: number;
}

interface PageReadinessHistoryRow {
  url: string;
  status: string;
  pageType?: string | null;
  overallScore?: number | null;
  indexabilityScore?: number | null;
  contentScore?: number | null;
  technicalScore?: number | null;
  aiSearchabilityScore?: number | null;
  criticalIssues: number;
  majorIssues: number;
  minorIssues: number;
  checkCount: number;
  scopeCount: number;
}

interface PageReadinessHistorySnapshot {
  date: string;
  archiveReason: string;
  archivedAt: string;
  recordCount: number;
  pages: PageReadinessHistoryRow[];
}

interface PageReadinessHistoryResponse {
  siteId: number;
  daysRequested: number;
  limit: number;
  snapshots: PageReadinessHistorySnapshot[];
}

const PILLAR_COLORS = {
  overall: '#6366f1',
  indexability: '#f59e0b',
  contentIntent: '#8b5cf6',
  technicalHygiene: '#14b8a6',
  internalLinking: '#3b82f6',
  performance: '#06b6d4',
  aiSearchability: '#ec4899',
};

const PAGE_COLORS = {
  ready: '#16a34a',
  partial: '#f59e0b',
  notReady: '#dc2626',
};

const ACTION_COLORS = {
  openAffected: '#dc2626',
  resolvedAffected: '#16a34a',
  openActions: '#f59e0b',
};

const formatDate = (date: string) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

const signedNumber = (value: number) => value > 0 ? `+${value}` : `${value}`;

const scoreColor = (value: number, inverse = false) => {
  if (value === 0) return 'text.primary';
  const good = inverse ? value < 0 : value > 0;
  return good ? 'success.main' : 'error.main';
};

const statusChipColor = (status?: string): 'success' | 'warning' | 'error' | 'default' => {
  const normalized = (status || '').toLowerCase();
  if (normalized === 'ready') return 'success';
  if (normalized === 'needs_improvement') return 'warning';
  if (normalized) return 'error';
  return 'default';
};

const formatScore = (value?: number | null) => value == null ? '-' : Math.round(value).toString();

const errorMessage = (err: unknown) => {
  if (typeof err === 'object' && err !== null && 'response' in err) {
    const response = (err as { response?: { data?: { message?: string } } }).response;
    return response?.data?.message;
  }
  return undefined;
};

export default function SiteReadinessHistoryPage() {
  const navigate = useNavigate();
  const { selectedSite } = useSiteStore();
  const [siteReadinessHistory, setSiteReadinessHistory] = useState<SiteReadinessHistoryPoint[]>([]);
  const [pageReadinessHistory, setPageReadinessHistory] = useState<PageReadinessHistorySnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageHistoryError, setPageHistoryError] = useState<string | null>(null);
  const [days, setDays] = useState(30);
  const [selectedMetric, setSelectedMetric] = useState<string>('overall');
  const [selectedPageSnapshotIndex, setSelectedPageSnapshotIndex] = useState(0);

  useEffect(() => {
    if (!selectedSite) return;

    const fetchReadinessHistory = async () => {
      setLoading(true);
      setError(null);
      setPageHistoryError(null);
      try {
        const siteHistoryResponse = await api.get<SiteReadinessHistoryPoint[]>(`/earlystage/sites/${selectedSite.id}/readiness/history?days=${days}`);
        setSiteReadinessHistory(siteHistoryResponse.data);

        try {
          const pageHistoryResponse = await api.get<PageReadinessHistoryResponse>(`/earlystage/sites/${selectedSite.id}/readiness/page-history?days=${days}&limit=100`);
          setPageReadinessHistory(pageHistoryResponse.data.snapshots || []);
          setSelectedPageSnapshotIndex(0);
        } catch (pageErr: unknown) {
          console.error('Error fetching page readiness snapshot history:', pageErr);
          setPageReadinessHistory([]);
          setSelectedPageSnapshotIndex(0);
          setPageHistoryError('Page readiness snapshots are temporarily unavailable. Site readiness history is still shown.');
        }
      } catch (err: unknown) {
        console.error('Error fetching readiness history:', err);
        setError(errorMessage(err) || 'Failed to load readiness history');
      } finally {
        setLoading(false);
      }
    };

    fetchReadinessHistory();
  }, [selectedSite, days]);

  if (!selectedSite) {
    return (
      <Box sx={{
        maxWidth: { xs: 400, sm: 768, md: 900, lg: 1200 },
        mx: 'auto',
        px: { xs: 2, sm: 3, md: 4 },
        py: 3
      }}>
        <Alert severity="info">Please select a site to view readiness history</Alert>
      </Box>
    );
  }

  const chartData = siteReadinessHistory.map(point => ({
    date: formatDate(point.date),
    overall: point.overallScore,
    indexability: point.pillars.indexability,
    contentIntent: point.pillars.contentIntent,
    technicalHygiene: point.pillars.technicalHygiene,
    internalLinking: point.pillars.internalLinking,
    performance: point.pillars.performance,
    aiSearchability: point.pillars.aiSearchability,
    readyPages: point.readyPageCount || 0,
    partialPages: point.partialPageCount || 0,
    notReadyPages: point.notReadyPageCount || 0,
    openAffectedPages: point.openAffectedPageCount || 0,
    resolvedAffectedPages: point.resolvedAffectedPageCount || 0,
    openActions: point.openActionCount || 0,
  })).reverse(); // Reverse to show oldest to newest

  const getProgressSummary = () => {
    if (siteReadinessHistory.length < 2) return null;
    const latest = siteReadinessHistory[0];
    const baseline = siteReadinessHistory[siteReadinessHistory.length - 1];
    const scoreChange = latest.overallScore - baseline.overallScore;
    return {
      latest,
      baseline,
      scoreChange,
      readyPageChange: (latest.readyPageCount || 0) - (baseline.readyPageCount || 0),
      notReadyPageChange: (latest.notReadyPageCount || 0) - (baseline.notReadyPageCount || 0),
      openAffectedPageChange: (latest.openAffectedPageCount || 0) - (baseline.openAffectedPageCount || 0),
      resolvedAffectedPageChange: (latest.resolvedAffectedPageCount || 0) - (baseline.resolvedAffectedPageCount || 0),
      percent: baseline.overallScore > 0
        ? Math.round((scoreChange / baseline.overallScore) * 100)
        : 0,
    };
  };

  const progressSummary = getProgressSummary();
  const latestSnapshot = siteReadinessHistory[0];
  const latestPageSnapshot = pageReadinessHistory[0];
  const previousSnapshot = siteReadinessHistory.length > 1 ? siteReadinessHistory[1] : null;
  const hasTrackingCounts = siteReadinessHistory.some(point =>
    (point.readyPageCount || 0) > 0 ||
    (point.partialPageCount || 0) > 0 ||
    (point.notReadyPageCount || 0) > 0 ||
    (point.openAffectedPageCount || 0) > 0 ||
    (point.resolvedAffectedPageCount || 0) > 0 ||
    (point.openActionCount || 0) > 0
  );
  const lastScanSummary = latestSnapshot && previousSnapshot ? {
    scoreChange: latestSnapshot.overallScore - previousSnapshot.overallScore,
    readyPageChange: (latestSnapshot.readyPageCount || 0) - (previousSnapshot.readyPageCount || 0),
    notReadyPageChange: (latestSnapshot.notReadyPageCount || 0) - (previousSnapshot.notReadyPageCount || 0),
    openAffectedPageChange: (latestSnapshot.openAffectedPageCount || 0) - (previousSnapshot.openAffectedPageCount || 0),
    resolvedAffectedPageChange: (latestSnapshot.resolvedAffectedPageCount || 0) - (previousSnapshot.resolvedAffectedPageCount || 0),
  } : null;
  const readyRatio = latestSnapshot?.pagesAnalyzed
    ? Math.round(((latestSnapshot.readyPageCount || 0) / latestSnapshot.pagesAnalyzed) * 100)
    : 0;
  const selectedPageSnapshot = pageReadinessHistory[selectedPageSnapshotIndex] || latestPageSnapshot;
  const selectedArchivedReadyPages = selectedPageSnapshot?.pages.filter(page => page.status?.toLowerCase() === 'ready').length || 0;
  const selectedArchivedProblemPages = selectedPageSnapshot
    ? selectedPageSnapshot.pages.length - selectedArchivedReadyPages
    : 0;
  const selectedArchivedIssueCount = selectedPageSnapshot?.pages.reduce(
    (total, page) => total + page.criticalIssues + page.majorIssues + page.minorIssues,
    0
  ) || 0;
  const selectedArchivedEvidenceCount = selectedPageSnapshot?.pages.reduce(
    (total, page) => total + page.checkCount + page.scopeCount,
    0
  ) || 0;

  return (
    <Box sx={{
      maxWidth: { xs: 400, sm: 768, md: 900, lg: 1200 },
      mx: 'auto',
      px: { xs: 2, sm: 3, md: 4 },
      py: 3
    }}>
      {/* Header */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/early-stage')}
        sx={{ mb: 2 }}
      >
        Back to Dashboard
      </Button>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUpIcon />
            Progress Tracking
          </Typography>
          <Typography variant="body2" color="text.secondary">
            See what changed between real scan snapshots and whether open issues are shrinking over time.
          </Typography>
        </Box>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Time Period</InputLabel>
          <Select
            value={days}
            label="Time Period"
            onChange={(e) => setDays(Number(e.target.value))}
          >
            <MenuItem value={7}>Last 7 days</MenuItem>
            <MenuItem value={14}>Last 14 days</MenuItem>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={60}>Last 60 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && siteReadinessHistory.length === 0 && (
        <Alert severity="info">
          No history data available yet. Historical data will be collected daily as you use the platform.
        </Alert>
      )}

      {!loading && !error && siteReadinessHistory.length > 0 && (
        <>
          {!hasTrackingCounts && (
            <Alert severity="info" sx={{ mb: 3 }}>
              Older snapshots contain score history but not page/action tracking counts. New snapshots will show page readiness movement and Action Center impact.
            </Alert>
          )}

          {/* Progress Summary */}
          {progressSummary && (
            <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      Scan Tracking Summary
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Latest scan compared with actual previous snapshots, not calendar days.
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: 220 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Latest scan: {formatDate(progressSummary.latest.date)} · {progressSummary.latest.pagesAnalyzed} pages analyzed
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={readyRatio}
                      sx={{ height: 8, borderRadius: 999 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {readyRatio}% pages ready
                    </Typography>
                  </Box>
                </Box>

                {lastScanSummary && (
                  <Box sx={{ mb: 2, p: 2, borderRadius: 2, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.100' }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                      Since last scan ({formatDate(previousSnapshot!.date)} → {formatDate(latestSnapshot!.date)})
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Typography variant="h5" color={scoreColor(lastScanSummary.scoreChange)}>{signedNumber(lastScanSummary.scoreChange)}</Typography>
                        <Typography variant="caption" color="text.secondary">Score</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="h5" color={scoreColor(lastScanSummary.readyPageChange)}>{signedNumber(lastScanSummary.readyPageChange)}</Typography>
                        <Typography variant="caption" color="text.secondary">Ready pages</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="h5" color={scoreColor(lastScanSummary.notReadyPageChange, true)}>{signedNumber(lastScanSummary.notReadyPageChange)}</Typography>
                        <Typography variant="caption" color="text.secondary">Not-ready pages</Typography>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Typography variant="h5" color={scoreColor(lastScanSummary.openAffectedPageChange, true)}>{signedNumber(lastScanSummary.openAffectedPageChange)}</Typography>
                        <Typography variant="caption" color="text.secondary">Open affected pages</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="h4" color={scoreColor(progressSummary.scoreChange)}>
                        {signedNumber(progressSummary.scoreChange)}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>Readiness score</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {signedNumber(progressSummary.percent)}% since {formatDate(progressSummary.baseline.date)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="h4" color={scoreColor(progressSummary.readyPageChange)}>
                        {signedNumber(progressSummary.readyPageChange)}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>Ready pages</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {progressSummary.latest.readyPageCount || 0} ready now
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="h4" color={scoreColor(progressSummary.notReadyPageChange, true)}>
                        {signedNumber(progressSummary.notReadyPageChange)}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>Not-ready pages</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {progressSummary.latest.notReadyPageCount || 0} still need work
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="h4" color={scoreColor(progressSummary.openAffectedPageChange, true)}>
                        {signedNumber(progressSummary.openAffectedPageChange)}
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>Open affected pages</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {progressSummary.resolvedAffectedPageChange > 0 ? `${progressSummary.resolvedAffectedPageChange} resolved in period` : `${progressSummary.latest.openAffectedPageCount || 0} open now`}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} lg={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Page Readiness Movement
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Shows whether analyzed pages are moving from not-ready to ready across scans.
                  </Typography>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="readyPages" stackId="pages" name="Ready" fill={PAGE_COLORS.ready} />
                      <Bar dataKey="partialPages" stackId="pages" name="Needs improvement" fill={PAGE_COLORS.partial} />
                      <Bar dataKey="notReadyPages" stackId="pages" name="Not ready" fill={PAGE_COLORS.notReady} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} lg={6}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Action Center Impact
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Tracks how many pages remain affected by open actions versus resolved actions.
                  </Typography>
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="openAffectedPages" name="Open affected pages" stroke={ACTION_COLORS.openAffected} strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="resolvedAffectedPages" name="Resolved affected pages" stroke={ACTION_COLORS.resolvedAffected} strokeWidth={2} dot={{ r: 3 }} />
                      <Line type="monotone" dataKey="openActions" name="Open actions" stroke={ACTION_COLORS.openActions} strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card sx={{ mt: 3, border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Page Readiness Snapshot History
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Inspect archived page-level readiness evidence for each scan snapshot.
                  </Typography>
                </Box>
                {selectedPageSnapshot && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Chip label={`${formatDate(selectedPageSnapshot.date)} snapshot`} size="small" color="primary" variant="outlined" />
                    <Chip label={`${pageReadinessHistory.length} batches`} size="small" variant="outlined" />
                    <Chip label={`${selectedArchivedProblemPages} pages need work`} size="small" color={selectedArchivedProblemPages > 0 ? 'warning' : 'success'} variant="outlined" />
                  </Box>
                )}
              </Box>

              {pageHistoryError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {pageHistoryError}
                </Alert>
              )}

              {!selectedPageSnapshot && !pageHistoryError && (
                <Alert severity="info">
                  No page readiness archive snapshots are available for this period yet.
                </Alert>
              )}

              {selectedPageSnapshot && (
                <>
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Page Snapshot</InputLabel>
                        <Select
                          value={selectedPageSnapshotIndex}
                          label="Page Snapshot"
                          onChange={(e) => setSelectedPageSnapshotIndex(Number(e.target.value))}
                        >
                          {pageReadinessHistory.map((snapshot, index) => (
                            <MenuItem key={`${snapshot.date}-${snapshot.archivedAt}-${index}`} value={index}>
                              {formatDate(snapshot.date)} · {snapshot.archiveReason} · {snapshot.pages.length} pages
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="h6">{selectedPageSnapshot.recordCount}</Typography>
                        <Typography variant="caption" color="text.secondary">Archived pages</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="h6" color="success.main">{selectedArchivedReadyPages}</Typography>
                        <Typography variant="caption" color="text.secondary">Ready pages</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="h6" color={selectedArchivedProblemPages > 0 ? 'warning.main' : 'success.main'}>{selectedArchivedProblemPages}</Typography>
                        <Typography variant="caption" color="text.secondary">Need work</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="h6" color={selectedArchivedIssueCount > 0 ? 'error.main' : 'success.main'}>{selectedArchivedIssueCount}</Typography>
                        <Typography variant="caption" color="text.secondary">Issues found</Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Archive reason: {selectedPageSnapshot.archiveReason} · {selectedPageSnapshot.pages.length} rows returned
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Evidence captured: {selectedArchivedEvidenceCount} checks/scopes
                    </Typography>
                  </Box>
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Page</TableCell>
                          <TableCell align="center">Status</TableCell>
                          <TableCell align="right">Score</TableCell>
                          <TableCell align="right">AI</TableCell>
                          <TableCell align="right">Issues</TableCell>
                          <TableCell align="right">Evidence</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedPageSnapshot.pages.slice(0, 50).map((page) => (
                          <TableRow key={`${selectedPageSnapshot.date}-${page.url}`} hover>
                            <TableCell sx={{ maxWidth: 460, wordBreak: 'break-word' }}>
                              <Typography variant="body2" fontWeight={600}>{page.url}</Typography>
                              <Typography variant="caption" color="text.secondary">{page.pageType || 'Page'}</Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={(page.status || 'unknown').replace(/_/g, ' ')}
                                size="small"
                                color={statusChipColor(page.status)}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">{formatScore(page.overallScore)}</TableCell>
                            <TableCell align="right">{formatScore(page.aiSearchabilityScore)}</TableCell>
                            <TableCell align="right">
                              {page.criticalIssues + page.majorIssues + page.minorIssues}
                            </TableCell>
                            <TableCell align="right">
                              {page.checkCount} checks · {page.scopeCount} scopes
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </CardContent>
          </Card>

          {/* Metric Selector */}
          <FormControl fullWidth sx={{ my: 3 }}>
            <InputLabel>Score Trend</InputLabel>
            <Select
              value={selectedMetric}
              label="Score Trend"
              onChange={(e) => setSelectedMetric(e.target.value)}
            >
              <MenuItem value="overall">Overall Score</MenuItem>
              <MenuItem value="all">All Pillars</MenuItem>
              <MenuItem value="indexability">Google Index Coverage</MenuItem>
              <MenuItem value="contentIntent">Content Intent</MenuItem>
              <MenuItem value="technicalHygiene">Technical Hygiene</MenuItem>
              <MenuItem value="internalLinking">Internal Linking</MenuItem>
              <MenuItem value="performance">Performance</MenuItem>
              <MenuItem value="aiSearchability">AI Searchability</MenuItem>
            </Select>
          </FormControl>

          {/* Trend Chart */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Score Trend
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Score is supporting context. The primary tracking signal is whether page issues and open affected pages are moving down.
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  {(selectedMetric === 'overall' || selectedMetric === 'all') && (
                    <Line type="monotone" dataKey="overall" stroke={PILLAR_COLORS.overall} strokeWidth={2} name="Overall" dot={{ r: 4 }} />
                  )}
                  {(selectedMetric === 'indexability' || selectedMetric === 'all') && (
                    <Line type="monotone" dataKey="indexability" stroke={PILLAR_COLORS.indexability} strokeWidth={2} name="Google Index Coverage" dot={{ r: 3 }} />
                  )}
                  {(selectedMetric === 'contentIntent' || selectedMetric === 'all') && (
                    <Line type="monotone" dataKey="contentIntent" stroke={PILLAR_COLORS.contentIntent} strokeWidth={2} name="Content Intent" dot={{ r: 3 }} />
                  )}
                  {(selectedMetric === 'technicalHygiene' || selectedMetric === 'all') && (
                    <Line type="monotone" dataKey="technicalHygiene" stroke={PILLAR_COLORS.technicalHygiene} strokeWidth={2} name="Technical Hygiene" dot={{ r: 3 }} />
                  )}
                  {(selectedMetric === 'internalLinking' || selectedMetric === 'all') && (
                    <Line type="monotone" dataKey="internalLinking" stroke={PILLAR_COLORS.internalLinking} strokeWidth={2} name="Internal Linking" dot={{ r: 3 }} />
                  )}
                  {(selectedMetric === 'performance' || selectedMetric === 'all') && (
                    <Line type="monotone" dataKey="performance" stroke={PILLAR_COLORS.performance} strokeWidth={2} name="Performance" dot={{ r: 3 }} />
                  )}
                  {(selectedMetric === 'aiSearchability' || selectedMetric === 'all') && (
                    <Line type="monotone" dataKey="aiSearchability" stroke={PILLAR_COLORS.aiSearchability} strokeWidth={2} name="AI Searchability" dot={{ r: 3 }} />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
}
